import React, { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Caption,
  Card,
  Heading,
  Icon,
  List,
  ResourceList,
  Spinner,
  Stack,
  Tooltip,
} from '@shopify/polaris';
import {
  AlertMinor,
  CircleAlertMajor,
  TickMinor,
} from '@shopify/polaris-icons';
import {
  Manufacturer,
  UNKNOWN_MANUFACTURER,
} from '../../utils/types';
import { getStatusMessage } from '../../utils/order/orderStatus';
import OrderItem from './OrderItem';
import { OrderLineItem } from '../../queries/shopify/fragments/OrderLineItems';
import { OrderStatus } from '../../models/orderStatus';
import { SendStatus } from '../../models/sendStatus';

type ManufacturerGroupProps = {
  manufacturer: string;
  orderName: string;
  items: OrderLineItem[];
  statusObject: OrderStatus[Manufacturer];
  updateNotesHandler: (notes: any) => void;
  sendEmailHandler: () => Promise<void>;
  sendingAll: boolean;
  changeItemManufacturer: any;
};

const ManufacturerGroup = ({
  manufacturer,
  orderName,
  items,
  statusObject,
  updateNotesHandler,
  sendEmailHandler,
  sendingAll,
  changeItemManufacturer,
}: ManufacturerGroupProps) => {
  const { status, emails, time } = statusObject || {
    status: SendStatus.notSent,
  };

  const [loading, setLoading] = useState(sendingAll || false);
  const [notes, setNotes] = useState(statusObject?.notes || {});

  useEffect(() => {
    updateNotesHandler(notes);
  }, [notes]);

  async function onSendToBtnClicked() {
    setLoading(true);
    await sendEmailHandler();
    setLoading(false);
  }

  const statusDetailsTooltip = childComponent => {
    const dateLabel = (date: Date) =>
      `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;

    const content = () => (
      <div style={{ padding: 10 }}>
        <p>
          {status === SendStatus.sent
            ? 'Email sent'
            : 'Tried to send email'}{' '}
          on
        </p>
        <p>{dateLabel(new Date(time))}</p>

        {emails && emails.length ? (
          <div>
            <p style={{ marginBottom: '1.2rem' }}>to these emails:</p>
            <List type="bullet">
              {emails.map((email, i) => (
                <List.Item key={i} style={{ marginBottom: '0 !important' }}>
                  {email}
                </List.Item>
              ))}
            </List>
          </div>
        ) : null}
      </div>
    );

    return status !== SendStatus.notSent ? (
      <Tooltip content={content()} dismissOnMouseOut>
        {childComponent}
      </Tooltip>
    ) : (
      childComponent
    );
  };

  return (
    <Card>
      <Card.Section>
        <div>
          {manufacturer !== UNKNOWN_MANUFACTURER ? (
            <Stack alignment="center">
              <Stack.Item fill>
                <Heading>{manufacturer.toUpperCase()}</Heading>
              </Stack.Item>
              <Stack.Item>
                {status && status !== SendStatus.notSent
                  ? statusDetailsTooltip(
                    <Badge
                      icon={
                        status === SendStatus.sent
                          ? TickMinor
                          : AlertMinor
                      }
                      status={
                        status === SendStatus.sent
                          ? 'success'
                          : 'warning'
                      }
                    >
                      {getStatusMessage(status)}
                    </Badge>,
                  )
                  : null}
              </Stack.Item>
            </Stack>
          ) : (
            <Stack>
              <Stack.Item>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon source={CircleAlertMajor} color="red" />
                  <div style={{ marginLeft: 5 }} />
                  <Caption>
                    Unable to identify a manufacturer for these items. They will
                    not be emailed. Please, contact LiveArt team to resolve the
                    issue.
                  </Caption>
                </div>
              </Stack.Item>
            </Stack>
          )}
        </div>
      </Card.Section>

      <Card.Section>
        <ResourceList
          items={items}
          renderItem={item => {
            const note = {
              value: notes[item.id],
              update:
                // do not allow update if item already sent
                status !== SendStatus.sent
                  ? (value: string) => setNotes({ ...notes, ...{ [item.id]: value } })
                  : null,
            };

            return OrderItem({
              isSent: status === SendStatus.sent,
              orderName,
              item,
              note,
              changeItemManufacturer,
            });
          }}
        />

        <div style={{ marginTop: 10 }}>
          <Stack>
            <Stack.Item fill />
            {manufacturer !== UNKNOWN_MANUFACTURER ? (
              <Stack.Item>
                {statusDetailsTooltip(
                  <Button
                    size="slim"
                    disabled={loading || status === SendStatus.sent}
                    onClick={onSendToBtnClicked}
                  >
                    {loading ? (
                      <Spinner size="small" />
                    ) : (
                      `Send items to ${manufacturer.toUpperCase()}`
                    )}
                  </Button>,
                )}
              </Stack.Item>
            ) : null}
          </Stack>
        </div>
      </Card.Section>
    </Card>
  );
};

export default ManufacturerGroup;
